import { useCallback, useMemo } from "react";
import { useOfflineRead } from "@dowty/offline";
import { reads, type ReadCacheKey } from "../../../offline";
import { fetchDrinkCatalog, fetchRecentActivities } from "../../../offline/fetchers";
import { deriveRecentDrinkTemplates, DrinkActivitySummary, DrinkOption } from "../../../services/drinkService";
import { AuthUser } from "../../../context/AuthContext";

const EMPTY_DRINKS: DrinkOption[] = [];
const EMPTY_ACTIVITIES: DrinkActivitySummary[] = [];

export const useAddDrinkData = (
    user: AuthUser | null,
    isAuthenticated: boolean
) => {
    // Read-through cache (@dowty/offline): renders the
    // last successfully fetched drink catalog immediately, then refreshes from the network. This
    // is what makes logging a drink possible at all with no connection - previously this list was
    // fetched fresh on every mount with nothing cached, so it was simply empty offline (see
    // src/offline/fetchers.ts for fetchDrinkCatalog).
    const {
        data: drinksData,
        loading: fetching,
        isFromCache: drinksFromCache,
        error: drinksError,
    } = useOfflineRead<ReadCacheKey, DrinkOption[]>(reads, "drinks", fetchDrinkCatalog, []);
    const drinks = drinksData ?? EMPTY_DRINKS;

    // Same read-through cache, keyed per user, for the last 24h quick-add templates below. Not
    // essential for logging a drink (the amount/drink pickers work from the drinks cache alone),
    // but the quick-add shortcuts are otherwise silently empty offline too.
    const activitiesFetcher = useCallback(async () => {
        if (!user) return [];
        return fetchRecentActivities(user);
    }, [user]);
    const {
        data: activitiesData,
        error: activitiesError,
        loading: activitiesLoading,
        reload: reloadRecentActivities,
    } = useOfflineRead<ReadCacheKey, DrinkActivitySummary[]>(reads, `activities:${user?.userId ?? 0}`, activitiesFetcher, [user?.userId]);

    const recentActivities = useMemo(() => {
        return (isAuthenticated ? activitiesData ?? EMPTY_ACTIVITIES : EMPTY_ACTIVITIES)
            .filter((activity) => activity.timestamp)
            .sort((a, b) => (b.timestamp?.getTime() ?? 0) - (a.timestamp?.getTime() ?? 0));
    }, [activitiesData, isAuthenticated]);

    const recentTemplates = useMemo(
        () => deriveRecentDrinkTemplates(recentActivities, 24 * 60 * 60 * 1000),
        [recentActivities]
    );
    return {
        drinks, fetching, drinksFromCache, drinksError, recentActivities, recentTemplates,
        recentTemplatesLoading: activitiesLoading, recentTemplatesError: activitiesError, reloadRecentActivities,
    };
};
